import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Inbox, Columns3, Timer, Receipt, Users, Mail, LogOut, LifeBuoy,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Tickets", icon: Inbox, testid: "nav-tickets-tab", end: true },
  { to: "/kanban", label: "Task Board", icon: Columns3, testid: "nav-kanban-tab" },
  { to: "/time", label: "Timekeeper", icon: Timer, testid: "nav-time-tab" },
  { to: "/billing", label: "Billing", icon: Receipt, testid: "nav-billing-tab", admin: true },
  { to: "/agents", label: "Agents", icon: Users, testid: "nav-agents-tab", admin: true },
  { to: "/email-feed", label: "Email Inbound", icon: Mail, testid: "nav-email-feed-tab" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navItems = NAV.filter((n) => !n.admin || user.role === "admin");

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden md:flex w-60 flex-col border-r border-border bg-card/60 backdrop-blur-md sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-5 py-5 border-b border-border">
          <LifeBuoy className="h-6 w-6 text-orange-500" />
          <span className="font-extrabold tracking-tight text-lg">Chronodesk</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? "bg-orange-500/10 text-orange-400 border border-orange-500/30"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60 border border-transparent"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{user.name}</p>
              <span
                data-testid="user-role-badge"
                className="inline-block mt-0.5 font-mono-digits text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-secondary text-orange-400"
              >
                {user.role}
              </span>
            </div>
            <button
              data-testid="logout-button"
              onClick={() => { logout(); navigate("/login"); }}
              className="p-2 rounded-md text-muted-foreground hover:text-red-400 hover:bg-secondary/60 transition-colors"
              title="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="md:hidden flex items-center gap-2 px-4 py-3 border-b border-border bg-card/60 sticky top-0 z-20 overflow-x-auto">
          <LifeBuoy className="h-5 w-5 text-orange-500 shrink-0" />
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`mobile-${n.testid}`}
              className={({ isActive }) =>
                `px-2.5 py-1.5 rounded text-xs whitespace-nowrap ${
                  isActive ? "bg-orange-500/10 text-orange-400" : "text-muted-foreground"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
